import { cn } from '@/lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, UserRoundIcon, ShoppingCartIcon, Search } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
export function SiteNav() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    itemCount,
    openCart
  } = useCart();
  const handleCartClick = () => {
    openCart();
  };
  const handleSearchOpen = () => {
    setSearchOpen(true);
    if (location.pathname !== '/shop') navigate('/shop');
  };
  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchTerm.trim();
    setSearchOpen(false);
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : '/shop');
  };
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);
  useEffect(() => {
    if (!searchOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);
  const navLinks = [{
    name: 'Shop All',
    path: '/shop'
  }, {
    name: 'Performance',
    path: '/performance'
  }, {
    name: 'Gut Health',
    path: '/gut-health'
  }, {
    name: 'Shipping & Returns',
    path: '/shipping'
  }, {
    name: 'FAQ',
    path: '/faq'
  }];
  const isActive = (path: string) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const isShopPage = location.pathname === '/shop';
  const isPerformancePage = location.pathname.startsWith('/performance');
  const isGutHealthPage = location.pathname.startsWith('/gut-health');
  // Collection pages carry their own sub-brand lockup in the top-left.
  const logo = isPerformancePage ? {
    src: 'https://cdn.magicpatterns.com/uploads/cBZyuDV1gmpS1bSTeTuTCE/g1.png',
    alt: 'Structured Performance'
  } : isGutHealthPage ? {
    src: 'https://cdn.magicpatterns.com/uploads/oUicKgY8EnA3Nxysj3VdqM/Logo3.png',
    alt: 'Structured Gut Health'
  } : {
    src: 'https://cdn.magicpatterns.com/uploads/nUuBEYZR9wShMJbhvgZfkg/Logo2.png',
    alt: 'Structured Nutrition'
  };
  const bannerHeight = 32;
  // Pages whose hero image runs full-bleed behind the chrome can carry the nav
  // with no bar of its own; everywhere else the bar keeps the type legible.
  const isOverHero = location.pathname === '/' || isShopPage || isPerformancePage || isGutHealthPage || location.pathname.startsWith('/contact');
  const chromeBackground = isOverHero ? 'transparent' : '#1E1E1E';
  return <>
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-center text-xs font-bold tracking-wide" style={{
      height: `${bannerHeight}px`,
      backgroundColor: chromeBackground,
      backgroundImage: "none",
      color: "#ECEBEC"
    }} data-id="element-3499" data-mp="2ggsr6yxwgf25">
        Free USA Shipping Over $99
      </div>
      <header className={`absolute left-0 right-0 z-50 h-[59px] ${isOverHero ? '' : 'shadow-[0_8px_18px_rgba(0,0,0,0.18)]'}`} style={{
      top: `${bannerHeight}px`,
      backgroundColor: chromeBackground
    }} data-id="element-3500" data-mp="1wuo1d2elx00t">
      <div className="h-full max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-6" data-id="element-3501" data-mp="18sspumka6ewl">
        {/* Logo */}
        <Link to="/" className="flex items-center group shrink-0" aria-label="Home" data-id="element-3502" data-mp="1nhovye2s1huk">
          <img src={logo.src} alt={logo.alt} className="h-10 w-auto object-contain" data-id="element-3503" data-mp="1io2tfeqbuuch" />
        </Link>

        {/* Desktop Nav (centered) */}
        <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center" data-id="element-3504" data-mp="1rh61t8m48h09">
          {navLinks.map(link => {
            const active = isActive(link.path);
            return <Link key={link.name} to={link.path} className={cn(`font-serif text-[13px] font-medium tracking-normal whitespace-nowrap transition-colors py-2 ${active ? 'underline decoration-2 underline-offset-[6px]' : 'hover:opacity-80'}`, "font-[600] font-[Avenir,_sans-serif]")} style={{
              color: "#ECEBEC"
            }} data-id="element-3505" data-mp="21gesnslopfie">
                {link.name}
              </Link>;
          })}
          <Link to="/contact" className={cn(`font-serif text-[13px] font-medium tracking-normal whitespace-nowrap transition-colors py-2 ${isActive('/contact') ? 'underline decoration-2 underline-offset-[6px]' : 'hover:opacity-80'}`, "font-[600] font-[Avenir,_sans-serif]")} style={{
            color: "#ECEBEC"
          }} data-id="element-3506" data-mp="2irtu0vkfzvmn">
            Support
          </Link>
        </nav>

        {/* Right side actions */}
        <div className="hidden lg:flex items-center gap-3 shrink-0" data-id="element-3507" data-mp="201ce5rkpkuip">
          <button type="button" onClick={handleSearchOpen} className="site-nav-icon-button inline-flex items-center justify-center w-8 h-8 overflow-hidden rounded-sm transition-colors" aria-label="Search products" aria-expanded={searchOpen} aria-controls="site-search" data-id="element-3508" data-mp="2424nslxht1fi">
            <Search className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3509" data-mp="3kejnprr98fxi" />
          </button>
          <Link to={user ? '/account?tab=profile' : '/login'} className="inline-flex items-center justify-center w-8 h-8 rounded-sm hover:bg-white/10 transition-colors" style={{
            color: '#ECEBEC'
          }} aria-label={user ? 'Account' : 'Sign in'} data-id="element-3510" data-mp="3pefsvubouden">
            <UserRoundIcon className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3511" data-mp="3iwcp1jwf4oli" />
          </Link>
          <button onClick={handleCartClick} className="site-nav-icon-button relative inline-flex items-center justify-center w-8 h-8 rounded-sm transition-colors" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-3512" data-mp="3dz31yawi4p0u">
            <ShoppingCartIcon className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3513" data-mp="5irkwn8h6d62" />
            {itemCount > 0 && <span className="absolute -top-1 -right-1 bg-[#ECEBEC] text-[#1E1E1E] text-[9px] font-bold rounded-full min-w-[14px] h-[14px] px-0.5 flex items-center justify-center" data-id="element-3514" data-mp="s1c100ubs6m">
                {itemCount}
              </span>}
          </button>
        </div>

        {/* Mobile actions */}
        <div className="lg:hidden flex items-center gap-1" data-id="element-3515" data-mp="ohlxpqodp3jl">
          <button onClick={handleCartClick} className="site-nav-icon-button relative inline-flex items-center justify-center w-9 h-9 rounded-sm transition-colors" aria-label={`Open cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`} data-id="element-3516" data-mp="og2hg02q2sht">
            <ShoppingCartIcon className="w-[18px] h-[18px]" strokeWidth={1.5} data-id="element-3517" data-mp="15qxy5ekg464c" />
            {itemCount > 0 && <span className="absolute top-0 right-0 bg-[#ECEBEC] text-[#1E1E1E] text-[9px] font-bold rounded-full min-w-[14px] h-[14px] px-0.5 flex items-center justify-center" data-id="element-3518" data-mp="1aq1554jzkmey">
                {itemCount}
              </span>}
          </button>
          <button className="site-nav-icon-button w-9 h-9 inline-flex items-center justify-center rounded-sm transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-3519" data-mp="1jpeelx841azo">
            {mobileMenuOpen ? <X className="w-[22px] h-[22px]" data-id="element-3520" data-mp="1tfqv9ect0k0j" /> : <Menu className="w-[22px] h-[22px]" data-id="element-3521" data-mp="1yfvgtiskk4xm" />}
          </button>
        </div>
      </div>

      {searchOpen && <div id="site-search" className="absolute top-full left-0 w-full border-t border-[#F0F0ED] bg-white px-6 py-3 shadow-[0_10px_18px_rgba(15,23,42,0.08)]" data-id="element-3522" data-mp="23dwzw3pzw493">
          <form onSubmit={handleSearchSubmit} className="max-w-7xl mx-auto" data-id="element-3523" data-mp="2jr3fi9qh9m5b">
            <label htmlFor="site-search-input" className="sr-only" data-id="element-3524" data-mp="2xqcs6zfh5y4j">
              Search products
            </label>
            <input ref={searchInputRef} id="site-search-input" type="search" value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder="Search" className="w-full rounded-md bg-[#F6F7F8] px-4 py-3 text-sm text-[#1F1F1F] placeholder:text-[#8A8A8A] outline-none transition-shadow focus:ring-2 focus:ring-[#172F3D]/30" data-id="element-3525" data-mp="2sqqj926oj41j" />
          </form>
        </div>}

      {/* Mobile Nav */}
      {mobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t border-[#D8D8D0] shadow-lg" data-id="element-3526" data-mp="1m9d4yi7zi9hd">
          <nav className="flex flex-col py-2" data-id="element-3527" data-mp="1ha0ezoltgmak">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`px-6 py-4 font-serif text-base font-medium tracking-normal transition-colors ${isActive(link.path) ? 'bg-[#D8D8D0] text-[#2D2D2D]' : 'text-[#2D2D2D] hover:bg-[#E8E8E5]'}`} data-id="element-3528" data-mp="3sy5czh2buppw">
                {link.name}
              </Link>)}
            <Link to="/contact" className={`px-6 py-4 font-serif text-base font-medium tracking-normal transition-colors ${isActive('/contact') ? 'bg-[#D8D8D0] text-[#2D2D2D]' : 'text-[#2D2D2D] hover:bg-[#E8E8E5]'}`} data-id="element-3529" data-mp="gj32pxcyhgda">
              Support
            </Link>
            <Link to={user ? '/account?tab=profile' : '/login'} className="px-6 py-4 font-serif text-base font-medium tracking-normal text-[#1F1F1F] hover:bg-[#E8E8E5] transition-colors border-t border-[#D8D8D0] flex items-center gap-2" data-id="element-3530" data-mp="bhvu5w2zod5k">
              <UserRoundIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-3531" data-mp="1buptn0nd9ora" />
              {user ? 'Account' : 'Sign In'}
            </Link>
          </nav>
        </div>}
    </header>
    </>;
}